/* ============================================================
   WP Cart Drawer — Behavior
   - Intercept Cart-Link clicks → open drawer
   - Fetch /cart.js on open
   - Quantity update via /cart/change.js
   - Remove item
   - Discount-Code via /discount/{code} navigation
   - Auto-open on 'wp:cart:added' event
   - Free-shipping progress calculation
   - Update header cart-badge live
   ============================================================ */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-wp-cart]');
  if (!drawer) return;

  var panel        = drawer.querySelector('.wp-cart__panel');
  var body         = drawer.querySelector('[data-wp-cart-body]');
  var itemsList    = drawer.querySelector('[data-wp-cart-items]');
  var foot         = drawer.querySelector('[data-wp-cart-foot]');
  var countLabel   = drawer.querySelector('[data-wp-cart-count-label]');
  var subtotalEl   = drawer.querySelector('[data-wp-cart-subtotal]');
  var checkoutPx   = drawer.querySelector('[data-wp-cart-checkout-price]');
  var shippingEl   = drawer.querySelector('[data-wp-cart-shipping]');
  var shippingFill = drawer.querySelector('[data-wp-cart-shipping-fill]');
  var shippingMsg  = drawer.querySelector('[data-wp-cart-shipping-msg]');
  var discountForm = drawer.querySelector('[data-wp-cart-discount-form]');
  var discountInput= drawer.querySelector('[data-wp-cart-discount-input]');
  var discountMsg  = drawer.querySelector('[data-wp-cart-discount-msg]');

  var freeShippingThreshold = shippingEl
    ? parseInt(shippingEl.getAttribute('data-threshold'), 10) || 4900
    : 4900;

  // ----------------------------------------
  // Open / Close
  // ----------------------------------------
  function open() {
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    // force reflow so transition kicks in
    void drawer.offsetWidth;
    drawer.setAttribute('data-open', '');
    document.body.classList.add('wp-cart-open');
    refresh(); // fetch fresh data
  }

  function close() {
    drawer.removeAttribute('data-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('wp-cart-open');
    setTimeout(function () {
      if (!drawer.hasAttribute('data-open')) drawer.hidden = true;
    }, 360);
  }

  // Close on backdrop, X-button, ESC
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-wp-cart-close]')) {
      e.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.hasAttribute('data-open')) close();
  });

  // ----------------------------------------
  // Cart-Link Intercept
  // ----------------------------------------
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href="/cart"], a[href$="/cart"], [data-wp-cart-link]');
    if (!link) return;
    // Allow ctrl/cmd-click to open in new tab
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    open();
  });

  // ----------------------------------------
  // Listen for Add-to-Cart events
  // ----------------------------------------
  window.addEventListener('wp:cart:added', function () { open(); });

  // ----------------------------------------
  // Money Formatter
  // ----------------------------------------
  function formatMoney(cents) {
    var euros = (cents / 100).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return euros + ' €';
  }

  // ----------------------------------------
  // Fetch Cart Data
  // ----------------------------------------
  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  // ----------------------------------------
  // Re-render Drawer
  // ----------------------------------------
  function refresh() {
    return fetchCart().then(function (cart) {
      // Update header cart-badge
      window.dispatchEvent(new CustomEvent('wp:cart:updated', {
        detail: { count: cart.item_count }
      }));

      // Update count
      if (countLabel) countLabel.textContent = '(' + cart.item_count + ')';

      // Update shipping progress
      updateShippingProgress(cart.total_price);

      // Update totals + checkout label
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      if (checkoutPx) checkoutPx.textContent = formatMoney(cart.total_price);

      // Update items (full re-render via section API for accuracy)
      return renderItems(cart);
    });
  }

  function renderItems(cart) {
    if (cart.item_count === 0) {
      // Empty state — re-fetch the section to get correct empty markup
      return fetch('/?section_id=wp-header', { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newBody = doc.querySelector('[data-wp-cart-body]');
          var newFoot = doc.querySelector('[data-wp-cart-foot]');
          if (newBody && body) body.innerHTML = newBody.innerHTML;
          if (newFoot && foot) foot.hidden = true;
        })
        .catch(function () {
          // Fallback inline empty state
          if (body) body.innerHTML = '<div class="wp-cart__empty"><h3 class="wp-cart__empty-title">Noch leer</h3><a href="/collections/all" class="wp-cart__empty-cta">Geschichten entdecken →</a></div>';
          if (foot) foot.hidden = true;
        });
    }

    // Items present — fetch section, transplant items + foot
    return fetch('/?section_id=wp-header', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newBody = doc.querySelector('[data-wp-cart-body]');
        var newFoot = doc.querySelector('[data-wp-cart-foot]');
        if (newBody && body) body.innerHTML = newBody.innerHTML;
        if (newFoot && foot) {
          foot.innerHTML = newFoot.innerHTML;
          foot.hidden = false;
          // Re-bind discount form (because we just replaced innerHTML)
          bindDiscountForm();
        }
      });
  }

  // ----------------------------------------
  // Update Shipping Progress
  // ----------------------------------------
  function updateShippingProgress(subtotal) {
    if (!shippingEl || !shippingFill || !shippingMsg) return;

    var pct = Math.min((subtotal / freeShippingThreshold) * 100, 100);
    shippingFill.style.width = pct + '%';

    var remaining = freeShippingThreshold - subtotal;

    if (remaining > 0) {
      shippingEl.removeAttribute('data-complete');
      shippingMsg.innerHTML = 'Nur noch <strong>' + formatMoney(remaining) + '</strong> bis zum kostenlosen Versand.';
    } else {
      shippingEl.setAttribute('data-complete', '');
      shippingMsg.innerHTML = '<span class="wp-cart__shipping-icon" aria-hidden="true">✓</span> Kostenloser Versand freigeschaltet.';
    }
  }

  // ----------------------------------------
  // Quantity Change
  // ----------------------------------------
  function changeQuantity(key, newQty) {
    var itemEl = drawer.querySelector('[data-cart-item-key="' + key + '"]');
    if (itemEl) itemEl.classList.add('is-removing');

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: newQty })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refresh(); });
  }

  // Delegate click handler for qty + remove buttons
  drawer.addEventListener('click', function (e) {
    var dec = e.target.closest('[data-wp-cart-qty-decrease]');
    var inc = e.target.closest('[data-wp-cart-qty-increase]');
    var rem = e.target.closest('[data-wp-cart-remove]');

    if (dec) {
      e.preventDefault();
      var key = dec.getAttribute('data-key');
      var item = dec.closest('[data-wp-cart-item]');
      var val  = item ? item.querySelector('[data-wp-cart-qty-value]') : null;
      var curr = val ? parseInt(val.textContent, 10) : 1;
      changeQuantity(key, Math.max(0, curr - 1));
    } else if (inc) {
      e.preventDefault();
      var key2 = inc.getAttribute('data-key');
      var item2 = inc.closest('[data-wp-cart-item]');
      var val2  = item2 ? item2.querySelector('[data-wp-cart-qty-value]') : null;
      var curr2 = val2 ? parseInt(val2.textContent, 10) : 1;
      changeQuantity(key2, curr2 + 1);
    } else if (rem) {
      e.preventDefault();
      var key3 = rem.getAttribute('data-key');
      changeQuantity(key3, 0);
    }
  });

  // ----------------------------------------
  // Discount Code
  // ----------------------------------------
  function bindDiscountForm() {
    var form = drawer.querySelector('[data-wp-cart-discount-form]');
    var input = drawer.querySelector('[data-wp-cart-discount-input]');
    var msg = drawer.querySelector('[data-wp-cart-discount-msg]');
    if (!form || !input) return;

    // Avoid binding twice
    if (form.dataset.wpBound === '1') return;
    form.dataset.wpBound = '1';

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var code = (input.value || '').trim();
      if (!code) return;

      // Shopify-Native: navigate to /discount/{code} which applies + redirects.
      // We send the user back to current page so the cart drawer can re-open and reflect the discount.
      var currentPath = window.location.pathname + window.location.search;
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(currentPath);
    });
  }

  bindDiscountForm();

  // ----------------------------------------
  // Auto-open via URL-Param (?open_cart=1 oder ?discount_applied=1)
  // Wird beim /cart-Redirect und nach Discount-Apply genutzt.
  // ----------------------------------------
  function cleanUrlParam(param) {
    if (!window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.delete(param);
    var cleaned = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
    window.history.replaceState({}, '', cleaned);
  }

  var params = new URLSearchParams(window.location.search);
  if (params.has('open_cart') || params.has('discount_applied')) {
    // Warte kurz bis DOM + andere Scripts ready sind
    setTimeout(function () {
      open();
      cleanUrlParam('open_cart');
      cleanUrlParam('discount_applied');
    }, 250);
  }

})();